import { useState, useEffect, useCallback, useRef } from 'react';
import './Page.css';
import Confetti from '../components/Confetti';
import StreakBadge from '../components/StreakBadge';
import ReminderSetting from '../components/ReminderSetting';
import StateService from '../services/StateService';

function Home() {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dayOffset, setDayOffset] = useState(0);
  const [slideDirection, setSlideDirection] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [slideInDirection, setSlideInDirection] = useState(null);
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);
  const [readyToSlideIn, setReadyToSlideIn] = useState(false);
  const [tickedPrompts, setTickedPrompts] = useState(() => StateService.getTickedPrompts());
  const [confettiTrigger, setConfettiTrigger] = useState(null);
  const isNavigatingRef = useRef(false);
  const tickIconRefs = useRef([null, null, null]);
  const confettiTimerRef = useRef(null);
  const loadingTimerRef = useRef(null);
  const slideInDelayRef = useRef(null);
  const slideInCleanupRef = useRef(null);

  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  const getDateLabel = (offset) => {
    if (offset === 0) {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return `${days[new Date().getDay()]}'s Prompts`;
    } else if (offset === -1) {
      return "Yesterday's Prompts";
    } else if (offset === 1) {
      return "Tomorrow's Prompts";
    } else {
      const date = new Date();
      date.setDate(date.getDate() + offset);
      const day = date.getDate();
      const months = ['January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'];
      const month = months[date.getMonth()];
      return `${day}${getOrdinalSuffix(day)} ${month}`;
    }
  };

  const getDayOfYear = (offset = 0) => {
    const now = new Date();
    now.setDate(now.getDate() + offset);
    const start = new Date(now.getFullYear(), 0, 0);
    const diff = now - start;
    const oneDay = 1000 * 60 * 60 * 24;
    return Math.floor(diff / oneDay);
  };

  const fetchPrompts = useCallback(async (offset, isNavigation = false) => {
    setLoading(true);
    setError(null);
    setShowLoadingIndicator(false);

    // Clear any existing timer
    if (loadingTimerRef.current) {
      clearTimeout(loadingTimerRef.current);
    }

    // Set delayed loading indicator (1.5s for initial, 3s for navigation)
    const delay = isNavigation ? 3000 : 1500;
    loadingTimerRef.current = setTimeout(() => {
      setShowLoadingIndicator(true);
    }, delay);

    try {
      const dayOfYear = getDayOfYear(offset);
      const apiBase = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiBase}/prompts?day=${dayOfYear}`);
      if (!response.ok) {
        throw new Error('Failed to fetch prompts');
      }
      const data = await response.json();
      setPrompts(data.prompts);
    } catch (err) {
      setError(err.message);
    } finally {
      // Clear the loading indicator timer
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      setShowLoadingIndicator(false);
      setLoading(false);
      if (isNavigation) {
        isNavigatingRef.current = false;
      }
    }
  }, []);

  useEffect(() => {
    fetchPrompts(dayOffset, isNavigatingRef.current);
  }, [dayOffset, fetchPrompts]);

  // When slide-in animation starts, set timer to clean up after it completes
  useEffect(() => {
    if (!loading && readyToSlideIn && slideInDirection) {
      slideInCleanupRef.current = setTimeout(() => {
        setSlideInDirection(null);
        setIsAnimating(false);
      }, 400);
    }
    return () => {
      if (slideInCleanupRef.current) {
        clearTimeout(slideInCleanupRef.current);
      }
    };
  }, [loading, readyToSlideIn, slideInDirection]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
      if (slideInDelayRef.current) {
        clearTimeout(slideInDelayRef.current);
      }
      if (slideInCleanupRef.current) {
        clearTimeout(slideInCleanupRef.current);
      }
      if (confettiTimerRef.current) {
        clearTimeout(confettiTimerRef.current);
      }
    };
  }, []);

  // Subscribe to StateService changes
  useEffect(() => {
    return StateService.subscribe(setTickedPrompts);
  }, []);

  const handleNavigate = (direction) => {
    if (isAnimating) return;

    setSlideDirection(direction);
    setIsAnimating(true);
    setReadyToSlideIn(false);
    isNavigatingRef.current = true;
    // When clicking left, prompts fly off right, new ones come from left
    // When clicking right, prompts fly off left, new ones come from right
    setSlideInDirection(direction === 'left' ? 'left' : 'right');

    // Clear any existing slide-in delay timer
    if (slideInDelayRef.current) {
      clearTimeout(slideInDelayRef.current);
    }

    setTimeout(() => {
      setDayOffset(prev => direction === 'left' ? prev - 1 : prev + 1);
      setSlideDirection(null);
    }, 400);

    // Allow slide-in after slide-out (400ms) + minimum pause (300ms)
    slideInDelayRef.current = setTimeout(() => {
      setReadyToSlideIn(true);
    }, 700);
  };

  const getContainerClass = () => {
    let className = 'prompts-container';
    // Slide-out animation (old prompts flying off)
    if (slideDirection === 'left') className += ' slide-out-right';
    if (slideDirection === 'right') className += ' slide-out-left';
    // Slide-in animation only after slide-out complete, minimum delay passed, and loading done
    if (!loading && !slideDirection && readyToSlideIn && slideInDirection === 'left') className += ' slide-in-left';
    if (!loading && !slideDirection && readyToSlideIn && slideInDirection === 'right') className += ' slide-in-right';
    return className;
  };

  const togglePromptTicked = (promptIndex) => {
    const dayKey = getDayOfYear(dayOffset);
    const currentlyTicked = StateService.isPromptTicked(dayKey, promptIndex);

    // Trigger confetti only when ticking (not unticking)
    if (!currentlyTicked) {
      // Clear any existing confetti timer
      if (confettiTimerRef.current) {
        clearTimeout(confettiTimerRef.current);
      }
      setConfettiTrigger({ index: promptIndex, timestamp: Date.now() });
      // Clear confetti trigger after animation completes (2s)
      confettiTimerRef.current = setTimeout(() => {
        setConfettiTrigger(null);
      }, 2000);
    }

    StateService.togglePromptTicked(dayKey, promptIndex);
  };

  const isPromptTicked = (promptIndex) => {
    const dayKey = getDayOfYear(dayOffset);
    return StateService.isPromptTicked(dayKey, promptIndex);
  };

  // Show initial loading indicator only when not navigating and after delay
  const isInitialLoading = loading && !slideInDirection;
  // Show carousel when: not initial loading AND no error
  const showCarousel = !isInitialLoading && !error;
  // During navigation, wait for slide-in only AFTER slide-out is complete (slideDirection is null)
  const waitingForSlideIn = slideInDirection && !slideDirection && (!readyToSlideIn || loading);
  // Show navigation loading indicator inside carousel (only after 3s delay)
  const isNavigationLoading = waitingForSlideIn && showLoadingIndicator;

  return (
    <main className="page">
      <div className="home-header">
        <h1>{getDateLabel(dayOffset)}</h1>
        <div className="home-header-actions">
          <StreakBadge />
          <ReminderSetting />
        </div>
      </div>
      {isInitialLoading && showLoadingIndicator && <p className="loading">Loading prompts...</p>}
      {error && <p className="error">Error: {error}</p>}
      {confettiTrigger && (
        <Confetti
          trigger={confettiTrigger.timestamp}
          originRef={{ current: tickIconRefs.current[confettiTrigger.index] }}
        />
      )}
      {showCarousel && (
        <div className="prompts-carousel">
          <button
            className="nav-arrow nav-arrow-left"
            onClick={() => handleNavigate('left')}
            disabled={isAnimating}
            aria-label="Previous day"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <div className={getContainerClass()}>
            {waitingForSlideIn ? (
              isNavigationLoading && <p className="loading navigation-loading">Loading prompts...</p>
            ) : (
              prompts.map((prompt, index) => (
                <div
                  key={index}
                  className={`prompt${isPromptTicked(index) ? ' ticked' : ''}`}
                  onClick={() => togglePromptTicked(index)}
                >
                  <span className="prompt-text">{prompt.text}</span>
                  <svg
                    ref={el => tickIconRefs.current[index] = el}
                    className="tick-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
              ))
            )}
          </div>
          <button
            className="nav-arrow nav-arrow-right"
            onClick={() => handleNavigate('right')}
            disabled={isAnimating}
            aria-label="Next day"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
      )}
    </main>
  );
}

export default Home;
